import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../shared/layout/titles/PageTitle';
import PageContent from '../../../shared/layout/containers/PageContent';
import MainButton from '../../../shared/layout/buttons/MainButton';
import { fetchAdminAlerts, acknowledgeAdminAlert, resolveAdminAlert } from '../../../services/Http/AdminApi';
import AdminFilterBar from './components/AdminFilterBar';
import AdminDataTable from './components/AdminDataTable';
import AlertPill from './components/AlertPill';
import styled from 'styled-components';
import { toast } from 'react-toastify';

const AdminAlerts = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetchAdminAlerts({ search });
            setRows(response?.data || []);
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Failed to load alerts');
        } finally {
            setLoading(false);
        }
    }, [search]);

    useEffect(() => {
        load();
    }, [load]);

    const columns = useMemo(() => ([
        { key: 'title', label: 'Alert' },
        { key: 'source', label: 'Source' },
        { key: 'type', label: 'Type' },
        {
            key: 'status',
            label: 'Status',
            render: (row) => <AlertPill severity={row.severity} status={row.status} />,
        },
        { key: 'created_at', label: 'Created', render: (row) => row.created_at ? new Date(row.created_at).toLocaleString() : '—' },
        {
            key: 'actions',
            label: 'Actions',
            render: (row) => (
                <Actions>
                    {row.status !== 'acknowledged' && row.status !== 'resolved' && (
                        <MainButton size="small" variant="secondary" onClick={() => handleAcknowledge(row.id)}>Acknowledge</MainButton>
                    )}
                    {row.status !== 'resolved' && (
                        <MainButton size="small" variant="danger" onClick={() => handleResolve(row.id)}>Resolve</MainButton>
                    )}
                </Actions>
            ),
        },
    ]), []);

    const handleAcknowledge = async (id) => {
        try {
            await acknowledgeAdminAlert(id);
            toast.success('Alert acknowledged');
            load();
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Failed to acknowledge alert');
        }
    };

    const handleResolve = async (id) => {
        try {
            await resolveAdminAlert(id);
            toast.success('Alert resolved');
            load();
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Failed to resolve alert');
        }
    };

    return (
        <PageContainer>
            <PageHeader>
                <PageTitle>Admin Alerts</PageTitle>
            </PageHeader>
            <PageContent>
                <AdminFilterBar>
                    <FilterGroup>
                        <SearchInput
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search alerts"
                        />
                    </FilterGroup>
                    <MainButton size="small" onClick={load}>Refresh</MainButton>
                </AdminFilterBar>
                <AdminDataTable columns={columns} rows={rows} loading={loading} emptyText="No alerts" />
            </PageContent>
        </PageContainer>
    );
};

export default AdminAlerts;

const FilterGroup = styled.div`
    display: flex;
    gap: 0.75rem;
    align-items: center;
    flex: 1;
`;

const SearchInput = styled.input`
    width: 100%;
    max-width: 320px;
    border: 1px solid ${({ theme }) => theme.colors.border || '#e5e7eb'};
    border-radius: 10px;
    padding: 0.5625rem 0.75rem;
    background: ${({ theme }) => theme.colors.surface || '#fff'};
    color: ${({ theme }) => theme.colors.textPrimary || '#111827'};
`;

const Actions = styled.div`
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
`;
